import { string } from "prop-types"
import { Redirect, Link } from "@reach/router"
import { Heading } from "@planningcenter/doxy-web"
import { Helmet } from "react-helmet"
import { usePersonFinder } from "source/directory/people/usePersonFinder"
import { NarrowLayout } from "source/shared/Layout"
import { Icon } from "source/shared/icons"
import { Avatar } from "source/shared/Avatar"
import { HouseholdAvatar } from "source/directory/HouseholdAvatar"
import {
  EmailField,
  AddressField,
  PhoneNumberField,
  BirthdayField,
  AnniversaryField,
} from "source/directory/people/Fields"
import { getRelationship } from "source/shared/getRelationship"
import { css } from "@emotion/react"
import { colors } from "source/shared/colors"
import {
  BreadcrumbNavigation,
  BreadcrumbPage,
  BreadcrumbDividerIcon,
} from "source/shared/BreadcrumbNavigation"
import { Error404 } from "source/shared/Error404"
import { useLocalStorage } from "source/hooks/useLocalStorage"
import { LAST_VIEW_STORAGE_KEY } from "source/directory/index"

PeopleShow.propTypes = {
  id: string,
}

export function PeopleShow({ id }) {
  const person = usePersonFinder(id)
  const [directoryLastView, _setDirectoryLastView] = useLocalStorage(
    LAST_VIEW_STORAGE_KEY,
    "people",
  )
  if (person.errors) {
    if (person.errors.some((error) => error.status === "404")) {
      return <Error404 />
    }

    return <Redirect to="/directory/people" default /> // We'll go back to directory if person won't load
  }
  const canEditPerson = person.meta.can_edit
  const individualAvatar = css`
    width: 152px;
    height: 152px;

    @media (max-width: 720px) {
      width: 80px;
      height: 80px;
    }
  `
  const profileFields = css`
    margin-left: 40%;
    @media (max-width: 600px) {
      margin-left: 0;
      align-items: center;
    }
  `
  const profileEmail = css`
    @media (max-width: 480px) {
      .emailField {
        word-break: break-all;
        text-align: left;
      }
    }
  `

  return (
    <NarrowLayout>
      <Helmet title={person.data.attributes.name} />
      <BreadcrumbNavigation>
        <BreadcrumbPage
          linkUrl={`/directory/${directoryLastView}`}
          pageText="Directory"
        />
        <BreadcrumbDividerIcon />
        <BreadcrumbPage pageText={person.data.attributes.name} isActive />
        {canEditPerson && (
          <div className="d-n d-b@sm ml-a">
            <Link
              className="ml-a btn secondary-btn minor-btn"
              to="/directory/edit"
            >
              Edit shared details
            </Link>
          </div>
        )}
      </BreadcrumbNavigation>

      {canEditPerson && (
        <div className="d-f jc-fe mb-1 d-n@sm">
          <Link
            className="ml-a btn secondary-btn minor-btn"
            to="/directory/edit"
          >
            Edit shared details
          </Link>
        </div>
      )}

      <div className="d-f fd-c ai-c">
        <div className="avatar mt-1" css={individualAvatar}>
          <Avatar
            url={person.data.attributes.avatar_url}
            alt={`profile image for ${name}`}
          />
        </div>
        <div className="mt-2 mb-1">
          <Heading level={1} text={person.data.attributes.name} />
        </div>
      </div>

      <div className="d-f fd-c mb-4" css={profileFields}>
        <BirthdayField birthday={person.data.attributes.birthday} />
        <AnniversaryField anniversary={person.data.attributes.anniversary} />
        {getRelationship(person, "phone_numbers").map((field) => (
          <PhoneNumberField field={field} key={field.id} />
        ))}
        <div css={profileEmail}>
          {getRelationship(person, "emails").map((field) => (
            <EmailField field={field} key={field.id} />
          ))}
        </div>
        {getRelationship(person, "addresses").map((field) => (
          <AddressField field={field} key={field.id} />
        ))}
      </div>

      <div className="d-f fd-c ai-c jc-c my-1">
        {getRelationship(person, "directory_households").map((household) => {
          const directoryProfileLink = css`
            &:hover {
              background-color: ${colors.tint8};
            }
          `
          return (
            <Link
              key={household.id}
              to={`/directory/households/${household.id}`}
              className="p-2 mb-1 d-f fd-cr fd-r@sm ai-c jc-sb w-100% action-drawer"
              css={directoryProfileLink}
            >
              <div className="d-f fd-c f-1">
                <div className="d-f ai-c mb-2p f-1">
                  <div className="mt-1 mt-0@sm ta-c ta-l@sm w-100% w-a@sm">
                    <Heading
                      level={3}
                      color="tint1"
                      text={household.attributes.name}
                    />
                  </div>
                  <span className="ml-1 c-tint3 d-n d-f@sm fs-5">
                    <Icon symbol="general#right-chevron" aria-hidden />
                  </span>
                </div>

                <div className="c-tint2 fs-4 ta-c ta-l@sm pr-2@sm">
                  {getRelationship(
                    { data: household, included: person.included },
                    "directory_people",
                  )
                    .map(
                      (household_member) =>
                        household_member.attributes.first_name,
                    )
                    .join(", ")}
                </div>
              </div>
              <div
                className="w-8 h-8"
                style={{
                  borderRadius: "32px",
                  overflow: "hidden",
                }}
              >
                <HouseholdAvatar
                  url={household.attributes.avatar_url}
                  alt={household.attributes.name}
                  size="thumbnail"
                />
              </div>
            </Link>
          )
        })}
      </div>
    </NarrowLayout>
  )
}
